import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PublicSlimHeader from '../../Common/PublicSlimHeader';
import EventLoadingState from '../Common/EventLoadingState';
import ProofAttachmentDropzone from '../../Common/ProofAttachmentDropzone';
import { formatMarketMoney } from '../../../utils/eventPricingDisplay';
import { uploadFilePublic } from '../../../services/fileApi';
import {
  getPublicSponsorshipPayment,
  initiatePublicSponsorshipCardPayment,
  submitPublicSponsorshipOfflinePayment,
  downloadPublicSponsorshipTaxInvoicePdf,
  downloadPublicSponsorshipReceiptPdf,
  getPublicEventSettings,
} from '../../../services/eventApi';
import { triggerBlobDownload } from '../../../utils/eventProformaStaffDownloads';

const money = (n, market) => formatMarketMoney(n, market || 'LOCAL');

const PublicSponsorshipPaymentPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const paycorpStatus = searchParams.get('paycorp');

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [method, setMethod] = useState('CARD');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [receiptDocs, setReceiptDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [payRes, settingsRes] = await Promise.all([
        getPublicSponsorshipPayment(token),
        getPublicEventSettings().catch(() => null),
      ]);
      setData(payRes.data?.data || null);
      const terms = settingsRes?.data?.data?.paymentAgreementText || '';
      setPaymentTerms(terms);
    } catch {
      toast.error('Payment link not found or expired');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  useEffect(() => {
    if (paycorpStatus === 'success') toast.success('Card payment received — thank you!');
    if (paycorpStatus === 'failed') toast.error('Card payment was not completed');
    if (paycorpStatus === 'cancelled') toast('Payment cancelled');
  }, [paycorpStatus]);

  const market = data?.pricingMarket || 'LOCAL';
  const amountDue = useMemo(() => Number(data?.amountDue ?? data?.taxInvoice?.grandTotal ?? 0), [data]);
  const isPaid = String(data?.paymentStatus || '').toUpperCase() === 'PAID';
  const pendingApproval = String(data?.paymentStatus || '').toUpperCase() === 'PENDING_APPROVAL';

  const onUploadSlip = async (fileOrEvent) => {
    const file = fileOrEvent?.target?.files?.[0] || fileOrEvent;
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFilePublic(file, {
        category: 'SPONSORSHIP_PAYMENT_RECEIPT',
        description: `Sponsorship payment receipt: ${data?.requestNumber || ''}`.trim(),
        title: file.name,
      });
      const doc = res.data?.data || res.data;
      setReceiptDocs([{
        fileId: doc.fileId || doc.id,
        fileName: doc.fileName || file.name,
        originalName: file.name,
        fileSize: file.size,
        fileType: file.type,
      }]);
      toast.success('Receipt uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const payCard = async () => {
    if (!agreed) { toast.error('Please accept the payment terms'); return; }
    setSubmitting(true);
    try {
      const res = await initiatePublicSponsorshipCardPayment(token, {
        amount: amountDue,
        paymentAgreement: { agreed: true },
      });
      const url = res.data?.data?.paymentPageUrl;
      if (url) window.location.href = url;
      else toast.error('Could not start card payment');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Card payment failed to start');
    } finally {
      setSubmitting(false);
    }
  };

  const payOffline = async () => {
    if (!agreed) { toast.error('Please accept the payment terms'); return; }
    if (!referenceNumber.trim() || !paymentDate || !receiptDocs.length) {
      toast.error('Reference, date, and payment slip are required');
      return;
    }
    setSubmitting(true);
    try {
      await submitPublicSponsorshipOfflinePayment(token, {
        method,
        amount: amountDue,
        referenceNumber,
        paymentDate,
        receiptDocuments: receiptDocs,
        paymentAgreement: { agreed: true },
      });
      toast.success('Payment submitted for verification');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadTax = async () => {
    try {
      const res = await downloadPublicSponsorshipTaxInvoicePdf(token);
      triggerBlobDownload(res.data, `${String(data?.taxInvoice?.invoiceNumber || 'tax-invoice').replace(/[/\\?%*:|"<>\s]+/g, '-')}.pdf`);
    } catch { toast.error('Tax invoice not available'); }
  };

  const downloadReceipt = async () => {
    try {
      const res = await downloadPublicSponsorshipReceiptPdf(token, 'latest');
      triggerBlobDownload(res.data, `${String([...(data?.paymentReceipts || [])].reverse().find((r) => r?.receiptNumber)?.receiptNumber || 'receipt').replace(/[/\\?%*:|"<>\s]+/g, '-')}.pdf`);
    } catch { toast.error('Receipt not available yet'); }
  };

  if (loading) return <EventLoadingState variant="page" message="Loading payment details..." />;
  if (!data) return <div className="min-h-screen bg-gray-50 p-8 text-center text-gray-600">Payment link unavailable.</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicSlimHeader title="Event sponsorship payment" subtitle={data.event?.name || 'SLIM Event'} logoUrl={data.eventLogoUrl} />
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-6">
        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <h1 className="text-xl font-bold text-gray-900">Sponsorship payment</h1>
          <p className="mt-1 text-sm text-gray-600">{data.sponsorName} · {data.requestNumber}</p>
          <p className="mt-4 text-2xl font-semibold text-gray-900">{money(amountDue, market)}</p>
          {isPaid && <p className="mt-2 text-sm font-medium text-green-700">Payment complete — thank you for your sponsorship.</p>}
          {pendingApproval && <p className="mt-2 text-sm font-medium text-violet-700">Your payment is pending staff approval.</p>}
        </div>

        {data.taxInvoice?.invoiceNumber && (
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={downloadTax} className="rounded-lg border px-4 py-2 text-sm">Download tax invoice</button>
            {(isPaid || data.paymentReceipts?.length) && (
              <button type="button" onClick={downloadReceipt} className="rounded-lg border px-4 py-2 text-sm">Download receipt</button>
            )}
          </div>
        )}

        {!isPaid && !pendingApproval && data.paymentLinkStatus === 'ACTIVE' && (
          <div className="rounded-xl border bg-white p-5 shadow-sm space-y-4">
            <div>
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1" />
                <span>I agree to the payment terms and conditions.</span>
              </label>
              {paymentTerms && <pre className="mt-2 whitespace-pre-wrap rounded bg-gray-50 p-3 text-xs text-gray-600">{paymentTerms}</pre>}
            </div>

            <div className="flex gap-2">
              {['CARD', 'BANK_TRANSFER', 'CHEQUE_DEPOSIT'].map((m) => (
                <button key={m} type="button" onClick={() => setMethod(m)} className={`rounded-lg border px-3 py-1.5 text-xs ${method === m ? 'border-indigo-600 bg-indigo-50 text-indigo-800' : ''}`}>
                  {m.replace('_', ' ')}
                </button>
              ))}
            </div>

            {method === 'CARD' ? (
              <button type="button" disabled={submitting} onClick={payCard} className="w-full rounded-lg bg-indigo-600 py-3 text-sm font-semibold text-white disabled:opacity-50">
                Pay by card
              </button>
            ) : (
              <div className="space-y-3">
                <input className="w-full rounded-lg border px-3 py-2 text-sm placeholder:text-gray-400" placeholder="Bank transfer or cheque reference" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} />
                <input type="date" className="w-full rounded-lg border px-3 py-2 text-sm text-gray-900" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} title="Payment date" />
                <ProofAttachmentDropzone
                  file={receiptDocs[0] || null}
                  required
                  hint={null}
                  busy={uploading}
                  onChange={(file) => {
                    if (!file) {
                      setReceiptDocs([]);
                      return;
                    }
                    onUploadSlip(file);
                  }}
                />
                <button type="button" disabled={submitting || uploading} onClick={payOffline} className="w-full rounded-lg bg-indigo-600 py-3 text-sm font-semibold text-white disabled:opacity-50">
                  Submit offline payment
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicSponsorshipPaymentPage;
